import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { markIsfTourCompleted } from "./isfPageTourStorage";
import "./IsfPageTour.scss";

const VIEWPORT_GAP = 12;
const TOOLTIP_GAP = 14;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getTargetRect(selector) {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 && rect.height <= 0) return null;
  return rect;
}

function computeTooltipStyle(rect, placement) {
  const maxWidth = 320;
  const estimatedHeight = 210;

  if (!rect || placement === "center") {
    return {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      maxWidth,
    };
  }

  // Prefer absolute top/left without negative transforms — those can clip above the viewport.
  let top = rect.bottom + TOOLTIP_GAP;
  let left = rect.left;
  let mode = placement || "bottom";

  if (mode === "right") {
    top = rect.top;
    left = rect.right + TOOLTIP_GAP;
  } else if (mode === "left") {
    top = rect.top;
    left = rect.left - maxWidth - TOOLTIP_GAP;
  } else if (mode === "top") {
    top = rect.top - TOOLTIP_GAP - estimatedHeight;
  }

  // If preferred side would clip, flip vertically.
  if (mode === "bottom" && top + estimatedHeight > window.innerHeight - VIEWPORT_GAP) {
    top = rect.top - TOOLTIP_GAP - estimatedHeight;
    mode = "top";
  }
  if ((mode === "top" || top < VIEWPORT_GAP) && rect.bottom + TOOLTIP_GAP + estimatedHeight <= window.innerHeight - VIEWPORT_GAP) {
    top = rect.bottom + TOOLTIP_GAP;
    mode = "bottom";
  }

  // Horizontal overflow for left/right placements.
  if (mode === "right" && left + maxWidth > window.innerWidth - VIEWPORT_GAP) {
    top = rect.bottom + TOOLTIP_GAP;
    left = rect.left;
  }
  if (mode === "left" && left < VIEWPORT_GAP) {
    top = rect.bottom + TOOLTIP_GAP;
    left = rect.left;
  }

  left = clamp(left, VIEWPORT_GAP, window.innerWidth - maxWidth - VIEWPORT_GAP);
  top = clamp(
    top,
    VIEWPORT_GAP,
    Math.max(VIEWPORT_GAP, window.innerHeight - estimatedHeight - VIEWPORT_GAP),
  );

  return { top, left, transform: "none", maxWidth };
}

export default function IsfPageTour({
  open = false,
  steps = [],
  staffId = "",
  onClose,
  onTourEnd,
  markTourCompleted,
}) {
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const stepIndexRef = useRef(0);
  const activeStepsRef = useRef([]);
  const prevOpenRef = useRef(false);
  const prevStepIndexRef = useRef(-1);

  const activeSteps = useMemo(() => steps.filter(Boolean), [steps]);
  activeStepsRef.current = activeSteps;

  const currentStep = activeSteps[stepIndex] || null;
  const isCenterStep = !currentStep?.target || currentStep.placement === "center";
  const isLastStep = stepIndex >= activeSteps.length - 1;

  const runStepLeave = useCallback((step) => {
    try {
      step?.onLeave?.();
    } catch {
      /* tour hook */
    }
  }, []);

  const runStepEnter = useCallback((step) => {
    try {
      step?.onEnter?.();
    } catch {
      /* tour hook */
    }
  }, []);

  const refreshTargetRect = useCallback(() => {
    const step = activeStepsRef.current[stepIndexRef.current];
    if (!step?.target) {
      setTargetRect(null);
      return;
    }
    const rect = getTargetRect(step.target);
    setTargetRect(rect);
    if (rect) {
      const el = document.querySelector(step.target);
      el?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    stepIndexRef.current = stepIndex;
  }, [stepIndex]);

  useEffect(() => {
    if (!open) {
      if (prevOpenRef.current) {
        const leavingStep = activeStepsRef.current[stepIndexRef.current];
        runStepLeave(leavingStep);
      }
      prevOpenRef.current = false;
      prevStepIndexRef.current = -1;
      setStepIndex(0);
      setTargetRect(null);
      return undefined;
    }

    if (!prevOpenRef.current) {
      prevOpenRef.current = true;
      prevStepIndexRef.current = -1;
      setStepIndex(0);
    }

    return undefined;
  }, [open, runStepLeave]);

  useEffect(() => {
    if (!open) return undefined;

    const step = activeStepsRef.current[stepIndex];
    if (!step) return undefined;

    if (prevStepIndexRef.current !== stepIndex) {
      if (prevStepIndexRef.current >= 0) {
        const leavingStep = activeStepsRef.current[prevStepIndexRef.current];
        runStepLeave(leavingStep);
      }
      runStepEnter(step);
      prevStepIndexRef.current = stepIndex;
    }

    refreshTargetRect();
    const enterTimer = window.setTimeout(refreshTargetRect, 120);
    const retryTimer = window.setTimeout(refreshTargetRect, 420);

    const onLayout = () => refreshTargetRect();
    window.addEventListener("resize", onLayout);
    window.addEventListener("scroll", onLayout, true);

    return () => {
      window.clearTimeout(enterTimer);
      window.clearTimeout(retryTimer);
      window.removeEventListener("resize", onLayout);
      window.removeEventListener("scroll", onLayout, true);
    };
  }, [
    open,
    stepIndex,
    runStepEnter,
    runStepLeave,
    refreshTargetRect,
  ]);

  const finishTour = useCallback(() => {
    const leavingStep = activeStepsRef.current[stepIndexRef.current];
    runStepLeave(leavingStep);
    onTourEnd?.();
    if (staffId) {
      const markComplete = markTourCompleted || markIsfTourCompleted;
      markComplete(staffId);
    }
    onClose?.();
  }, [staffId, onClose, onTourEnd, runStepLeave, markTourCompleted]);

  const goNext = () => {
    if (isLastStep) {
      finishTour();
      return;
    }
    setStepIndex((prev) => prev + 1);
  };

  const goBack = () => {
    setStepIndex((prev) => Math.max(0, prev - 1));
  };

  if (!open || !currentStep || !activeSteps.length) return null;

  const tooltipStyle = computeTooltipStyle(targetRect, currentStep.placement);
  const padding = 6;
  const highlightStyle = targetRect
    ? {
        top: Math.max(0, targetRect.top - padding),
        left: Math.max(0, targetRect.left - padding),
        width: targetRect.width + padding * 2,
        height: targetRect.height + padding * 2,
      }
    : null;

  return ReactDOM.createPortal(
    <div className="isf-page-tour" role="dialog" aria-modal="true" aria-labelledby="isf-page-tour-title">
      <div className="isf-page-tour__backdrop" onClick={finishTour} aria-hidden="true" />

      {!isCenterStep && highlightStyle ? (
        <div className="isf-page-tour__highlight" style={highlightStyle} />
      ) : null}

      <div
        className={`isf-page-tour__card${isCenterStep ? " is-center" : ""}`}
        style={tooltipStyle}
      >
        <div className="isf-page-tour__progress">
          Step {stepIndex + 1} of {activeSteps.length}
        </div>
        <h4 id="isf-page-tour-title" className="isf-page-tour__title">
          {currentStep.title}
        </h4>
        <p className="isf-page-tour__body">{currentStep.body}</p>
        <div className="isf-page-tour__actions">
          <button
            type="button"
            className="isf-page-tour__btn isf-page-tour__btn--ghost"
            onClick={finishTour}
          >
            Skip
          </button>
          <div className="isf-page-tour__actions-main">
            {stepIndex > 0 ? (
              <button
                type="button"
                className="isf-page-tour__btn isf-page-tour__btn--secondary"
                onClick={goBack}
              >
                Back
              </button>
            ) : null}
            <button
              type="button"
              className="isf-page-tour__btn isf-page-tour__btn--primary"
              onClick={goNext}
            >
              {isLastStep ? "Got it" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
